import { GeneralTheme, Gradient, NumberConfig } from './types';
import { BannerAspectRatio } from 'components/Popups/types';
import { useEffect, useState } from 'react';
import { EGradientDirection, ESaleDiscountDisplayType } from './enums';
import BigNumber from 'bignumber.js';
import { isAndroid, isIOS, isSafari } from 'react-device-detect';

export function useSetCssVars({
  font,
  buttonTextColor,
  buttonColor,
  bundleBorderColor,
  popupRatio = BannerAspectRatio.SQUARE,
  document,
}: {
  font?: string;
  buttonTextColor?: string;
  buttonColor?: Gradient;
  bundleBorderColor?: Gradient;
  popupRatio?: BannerAspectRatio;
  document: Document;
}): void {
  const defaultMap = {
    '--font-family': `'${font}'`,
    '--button-text-color': buttonTextColor,
    '--button-bg-color': buttonColor ? createGradientOrSolid(buttonColor) : '',
    '--bundle-border-color': bundleBorderColor
      ? createGradientOrSolid(bundleBorderColor)
      : '',
    '--popup-banner-ratio': popupRatio,
  };
  overrideCssVars(defaultMap, document);
}

export function overrideCssVars(
  cssToValuesMap: Record<string, any>,
  document: Document,
): void {
  const styleElement = document.createElement('style');
  let styles = 'body { ';

  Object.entries(cssToValuesMap).forEach(([key, value]) => {
    styles += `${key}: ${value}; `;
  });

  styles += '}';
  styleElement.textContent = styles;

  document.head.appendChild(styleElement);
}

export function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const updateIsDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    updateIsDesktop();
    window.addEventListener('resize', updateIsDesktop);
    return () => {
      window.removeEventListener('resize', updateIsDesktop);
    };
  }, []);

  return isDesktop;
}

export function useIsLandscape() {
  const [isLandscapeMobile, setIsLandscapeMobile] = useState(
    window.innerWidth >= 1024,
  );

  useEffect(() => {
    const updateIsLandscape = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setIsLandscapeMobile(height <= 500 && width > height);
    };

    updateIsLandscape();
    window.addEventListener('resize', updateIsLandscape);
    return () => {
      window.removeEventListener('resize', updateIsLandscape);
    };
  }, []);

  return isLandscapeMobile;
}

const mapDiscountToSign: Record<
  ESaleDiscountDisplayType,
  (salePercentage: number) => string
> = {
  [ESaleDiscountDisplayType.FIXED_AMOUNT]: (salePercentage: number) =>
    `+${salePercentage}`,
  [ESaleDiscountDisplayType.MULTIPLIER]: (salePercentage: number) =>
    `${salePercentage}x`,
  [ESaleDiscountDisplayType.PERCENTAGE]: (salePercentage: number) =>
    `${salePercentage}%`,
};

export const generateSalePercentageBasedOnType = (
  salePercentage: number,
  salePercentageDisplayType?: ESaleDiscountDisplayType,
) =>
  mapDiscountToSign[
    salePercentageDisplayType || ESaleDiscountDisplayType.PERCENTAGE
  ](salePercentage);

export const isWebviewBrowser = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return (
    userAgent.indexOf('instagram') > -1 ||
    userAgent.indexOf('fban') > -1 ||
    userAgent.indexOf('fbav') > -1 ||
    userAgent.indexOf('fb_iab') > -1 ||
    userAgent.indexOf('messenger') > -1
  );
};

export const moveProductGrid = ({
  subItemsGridElement,
  isDoneScrolling,
  setIsDoneScrolling,
  distance,
}: {
  subItemsGridElement: HTMLDivElement;
  isDoneScrolling: boolean;
  setIsDoneScrolling: (val: boolean) => void;
  distance?: number;
}) => {
  if (!isDoneScrolling) {
    const whatLeftToScroll =
      subItemsGridElement.scrollWidth - subItemsGridElement.scrollLeft;

    const movingDistance =
      whatLeftToScroll >= subItemsGridElement.clientWidth * 2
        ? subItemsGridElement.clientWidth
        : whatLeftToScroll - subItemsGridElement.clientWidth;

    subItemsGridElement.scrollBy({
      left: movingDistance,
      behavior: 'smooth',
    });

    if (whatLeftToScroll + 10 < subItemsGridElement.clientWidth * 2) {
      setIsDoneScrolling(true);
    }
  } else {
    subItemsGridElement.scrollTo({ left: 0, behavior: 'smooth' });
    setIsDoneScrolling(false);

    // Fix browser bug for setting the "scrollLeft" element to 0
    setTimeout(() => {
      subItemsGridElement.scrollLeft = 0;
    }, 500);
  }
};

export const createGradientOrSolid = (
  {
    colorOne,
    colorTwo,
    gradientDirection,
  }: {
    colorOne: string;
    colorTwo?: string;
    gradientDirection?: EGradientDirection;
  },
  isTripleGradient = false,
) => {
  if (colorOne && (colorTwo === 'false' || !colorTwo)) {
    return colorOne;
  }
  if (
    gradientDirection &&
    !Object.values(EGradientDirection).includes(gradientDirection)
  ) {
    gradientDirection = EGradientDirection.BottomToTop;
  }

  if (isTripleGradient) {
    return `linear-gradient(${
      gradientDirection || EGradientDirection.BottomToTop
    }, ${colorOne}, ${colorTwo}, ${colorOne})`;
  }

  return `linear-gradient(${
    gradientDirection || EGradientDirection.BottomToTop
  }, ${colorOne}, ${colorTwo})`;
};

export function useUrlQuery(query: string) {
  const queryParameters = new URLSearchParams(window.location.search);
  return queryParameters.get(query);
}

export function useUrlHash(query: string) {
  const queryParameters = new URLSearchParams(window.location.hash);
  return queryParameters.get(query);
}

export function useApplePay() {
  const isApplePayAvailable = (window as any)?.ApplePaySession;
  return { isApplePayAvailable };
}

const formattingCoundown = (values: number[]) => {
  if (values[0] > 0) {
    return `${values[0]}d ${values[1]}h ${values[2]}m ${values[3]}s`;
  } else if (values[1] > 0) {
    return `${values[1]}h ${values[2]}m ${values[3]}s`;
  } else if (values[2] > 0) {
    return `${values[2]}m ${values[3]}s`;
  } else return `${values[3]}s`;
};

const getReturnValues = (countDown: any) => {
  const days = Math.floor(countDown / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (countDown % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );
  const minutes = Math.floor((countDown % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((countDown % (1000 * 60)) / 1000);

  return formattingCoundown([days, hours, minutes, seconds]);
};

export function useCountdown(targerDate: number) {
  const [countDown, setCountDown] = useState(targerDate - new Date().getTime());

  useEffect(() => {
    const interval = setInterval(() => {
      setCountDown(targerDate - new Date().getTime());
    }, 1000);

    return () => clearInterval(interval);
  }, [targerDate]);

  if (countDown < 0) return 'Expired';
  return getReturnValues(countDown);
}

const mapDecimalPlaces: Record<number, number> = {
  [1]: 0,
  [100]: 2,
  [1000]: 3,
};

export function formattingNumber({
  number,
  config,
  multiplier,
  abbreviateWithDecimals = false,
}: {
  number: number | string;
  config?: NumberConfig;
  multiplier?: number;
  abbreviateWithDecimals?: boolean;
}): string {
  const isBigIntSupported = typeof BigInt !== 'undefined';
  const isInteger = Number.isInteger(number);
  if (!config) return number.toLocaleString();
  let formattedNumber = number.toString();
  const decimalPlaces = multiplier ? mapDecimalPlaces[multiplier] : 2;
  let abbreviationLetter = null;

  if (isBigIntSupported && !abbreviateWithDecimals && isInteger) {
    const num = BigInt(number);
    if (config.billionShorthand && num >= BigInt(1000000000)) {
      formattedNumber = (num / BigInt(1000000000)).toString();
      abbreviationLetter = 'B';
    } else if (config.millionShorthand && num >= BigInt(1000000)) {
      formattedNumber = (num / BigInt(1000000)).toString();
      abbreviationLetter = 'M';
    } else if (config.thousandShorthand && num >= BigInt(1000)) {
      formattedNumber = (num / BigInt(1000)).toString();
      abbreviationLetter = 'K';
    }
  } else {
    const num = new BigNumber(number);
    if (config.billionShorthand && num.greaterThanOrEqualTo(1000000000)) {
      formattedNumber = num.dividedBy(1000000000).toString();
      abbreviationLetter = 'B';
    } else if (config.millionShorthand && num.greaterThanOrEqualTo(1000000)) {
      formattedNumber = num.dividedBy(1000000).toString();
      abbreviationLetter = 'M';
    } else if (config.thousandShorthand && num.greaterThanOrEqualTo(1000)) {
      formattedNumber = num.dividedBy(1000).toString();
      abbreviationLetter = 'K';
    }
  }

  const parts = formattedNumber.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, config.milSeparator);

  if (abbreviateWithDecimals) {
    if (!parts[1]) {
      if (decimalPlaces === 0) {
        parts.length = 1;
      } else {
        parts[1] = '00';
      }
    } else if (parts[1].length < decimalPlaces) {
      parts[1] = parts[1].padEnd(decimalPlaces, '0');
    } else {
      parts[1] = parts[1].substring(0, decimalPlaces);
    }
  } else {
    if (parts[1]) parts[1] = parts[1].substring(0, 2);
  }

  formattedNumber = parts.join(config.fractionalSeparator);

  if (abbreviationLetter) formattedNumber += abbreviationLetter;

  return formattedNumber;
}

export const checkIfOlderOsVersions = (): boolean => {
  const ua = navigator?.userAgent?.toLowerCase();
  if (!ua) return true;

  const isChromeIOS = /crios/.test(ua);

  let versionMatch = ua.match(/version\/(\d+(\.\d+)?)/);

  if (!versionMatch) {
    versionMatch = ua.match(/crios\/(\d+(\.\d+)?)/); // Find version for Chrome
  }

  if (!versionMatch) return false; // Unable to extract version, consider it older

  const osMobileVersion = parseFloat(versionMatch[1]);
  if (isNaN(osMobileVersion)) return true; // Parsing failed, consider it older

  const SAFARI_IOS_THRESHOLD = 14.5;
  const CHROME_IOS_THRESHOLD = 92.1;
  const ANDROID_THRESHOLD = 123;

  return (
    ((isSafari || isIOS) && osMobileVersion < SAFARI_IOS_THRESHOLD) ||
    (isAndroid && osMobileVersion < ANDROID_THRESHOLD) ||
    (isChromeIOS && osMobileVersion < CHROME_IOS_THRESHOLD)
  );
};
